import { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowDown,
  ArrowUp,
  Check,
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  GripVertical,
  Pencil,
  Plus,
  Scissors,
  Trash2,
  X,
} from "lucide-react";
import t from "@/lang/lang";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Textarea } from "@/shared/components/ui/textarea";
import {
  Editor,
  EditorBold,
  EditorBulletList,
  EditorContent,
  EditorGesaGreen,
  EditorItalic,
  EditorOrderedList,
  EditorToolbar,
  EditorToolbarSeparator,
  EditorUnderline,
  editorGenerateHTML,
  useEditor,
} from "@/shared/components/editor/editor";
import { useQpmPestListQuery } from "@/services/backend/qpm/pests/api/pest-api";
import { useActiveCompany } from "@/shared/lib/active-company/active-company-provider";
import { PestKind } from "@/services/backend/qpm/pests/types/pest-enum";
import {
  TargetOrganismSelectionEditor,
  TargetOrganismSelectionOption,
} from "@/shared/components/domain/qpm/target-organism-selection-editor";
import {
  MonitorIoTClass,
  MonitorType,
} from "@/services/backend/qpm/types/enums";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/shared/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { cn } from "@/shared/lib/utils";
import {
  Collapsible,
  CollapsibleContent,
} from "@/shared/components/ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/shared/components/ui/alert-dialog";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

export interface ServiceProgramEntryValue {
  id: string;
  groupId: string;
  section: string;
  rowMode: string;
  pestCategoryMatchMode: ServiceProgramMatchModeValue;
  pestIndicatorMatchMode: ServiceProgramMatchModeValue;
  monitorTypeMatchMode: ServiceProgramMatchModeValue;
  ioTClassMatchMode: ServiceProgramMatchModeValue;
  order: number;
  body: string;
  pestCategoryKeys: string[];
  pestIndicatorKeys: string[];
  monitorTypes: MonitorType[];
  ioTClasses: MonitorIoTClass[];
}

export interface ServiceProgramGroupValue {
  id: string;
  section: string;
  order: number;
  title: string;
  subtitle: string;
}

export interface ServiceProgramPageBreakValue {
  id: string;
  anchorKind: "group" | "entry";
  anchorId: string;
  order: number;
}

type ServiceProgramMatchModeValue = "any" | "all";

const SERVICE_PROGRAM_MATCH_MODE_OPTIONS: Array<{
  value: ServiceProgramMatchModeValue;
  label: string;
}> = [
  { value: "any", label: t("Mindestens eine") },
  { value: "all", label: t("Alle") },
];

const SECTION_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "installation", label: t("Einrichtung") },
  { value: "maintenance", label: t("Pflege und Betreuung") },
  { value: "fly", label: t("UV Fluginsekten Vernichter") },
];

const MONITOR_TYPE_OPTIONS: Array<{ value: MonitorType; label: string }> = [
  { value: MonitorType.Checkpoint, label: t("Checkpoint") },
  { value: MonitorType.FlyExterminator, label: t("Fluginsekten Vernichter") },
  { value: MonitorType.IoT, label: t("IoT Monitor") },
];

const IOT_CLASS_OPTIONS: Array<{ value: MonitorIoTClass; label: string }> = [
  { value: MonitorIoTClass.IoTGateway, label: t("Gateway") },
  { value: MonitorIoTClass.IoTSim, label: t("SIM") },
  { value: MonitorIoTClass.IoTPortal, label: t("Portal") },
  { value: MonitorIoTClass.IoTSensor, label: t("Sensor") },
  { value: MonitorIoTClass.IoTPestMonitor, label: t("IoT Schädlingsmonitor") },
  {
    value: MonitorIoTClass.IoTPestMonitorWithSim,
    label: t("IoT Schädlingsmonitor mit SIM"),
  },
];

function serviceProgramSectionOrder(section: string): number {
  const index = SECTION_OPTIONS.findIndex((option) => option.value === section);
  if (index >= 0) {
    return index;
  }
  return SECTION_OPTIONS.length;
}

function normalizeStringKeys(values: string[]): string[] {
  return Array.from(
    new Set(values.map((v) => v.trim().toLowerCase()).filter((v) => v !== "")),
  ).sort((a, b) => a.localeCompare(b));
}

function normalizeEnumValues<T extends string>(values: T[]): T[] {
  return Array.from(new Set(values.filter((v) => v.trim() !== ""))).sort(
    (a, b) => a.localeCompare(b),
  ) as T[];
}

function normalizeEntry(
  entry: ServiceProgramEntryValue,
): ServiceProgramEntryValue {
  const normalizeMatchMode = (
    value: ServiceProgramMatchModeValue | string | undefined,
  ): ServiceProgramMatchModeValue => (value === "all" ? "all" : "any");
  return {
    ...entry,
    groupId: (entry.groupId ?? "").trim(),
    section: entry.section.trim().toLowerCase(),
    rowMode: "checkmark",
    pestCategoryMatchMode: normalizeMatchMode(entry.pestCategoryMatchMode),
    pestIndicatorMatchMode: normalizeMatchMode(entry.pestIndicatorMatchMode),
    monitorTypeMatchMode: normalizeMatchMode(entry.monitorTypeMatchMode),
    ioTClassMatchMode: normalizeMatchMode(entry.ioTClassMatchMode),
    body: entry.body ?? "",
    pestCategoryKeys: normalizeStringKeys(entry.pestCategoryKeys ?? []),
    pestIndicatorKeys: normalizeStringKeys(entry.pestIndicatorKeys ?? []),
    monitorTypes: normalizeEnumValues(entry.monitorTypes ?? []),
    ioTClasses: normalizeEnumValues(entry.ioTClasses ?? []),
  };
}

function emptyEntry(order: number): ServiceProgramEntryValue {
  return {
    id: crypto.randomUUID(),
    groupId: "",
    section: "maintenance",
    rowMode: "checkmark",
    pestCategoryMatchMode: "any",
    pestIndicatorMatchMode: "any",
    monitorTypeMatchMode: "any",
    ioTClassMatchMode: "any",
    order,
    body: "",
    pestCategoryKeys: [],
    pestIndicatorKeys: [],
    monitorTypes: [],
    ioTClasses: [],
  };
}

function MatchModeSelect({
  title,
  value,
  disabled,
  onChange,
}: {
  title: string;
  value: ServiceProgramMatchModeValue;
  disabled?: boolean;
  onChange: (next: ServiceProgramMatchModeValue) => void;
}) {
  return (
    <div className="grid gap-1">
      <Label className="text-xs font-medium">{title}</Label>
      <Select
        value={value}
        disabled={disabled}
        onValueChange={(next) => onChange(next as ServiceProgramMatchModeValue)}
      >
        <SelectTrigger className="h-9 w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {SERVICE_PROGRAM_MATCH_MODE_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="text-muted-foreground text-[11px]">
        {value === "all"
          ? t("Alle ausgewählten Werte dieser Bedingung müssen zutreffen.")
          : t(
              "Mindestens ein ausgewählter Wert dieser Bedingung muss zutreffen.",
            )}
      </p>
    </div>
  );
}

function normalizeGroup(
  group: ServiceProgramGroupValue,
): ServiceProgramGroupValue {
  return {
    ...group,
    id: (group.id ?? "").trim() || crypto.randomUUID(),
    section: (group.section ?? "maintenance").trim().toLowerCase(),
    title: group.title ?? "",
    subtitle: group.subtitle ?? "",
  };
}

function emptyGroup(order: number): ServiceProgramGroupValue {
  return {
    id: crypto.randomUUID(),
    section: "maintenance",
    order,
    title: "",
    subtitle: "",
  };
}

interface CreateGroupDraft {
  title: string;
  subtitle: string;
}

function emptyCreateGroupDraft(): CreateGroupDraft {
  return {
    title: "",
    subtitle: "",
  };
}

function valuesLabel<T extends string>(
  selected: T[],
  options: Array<{ value: T; label: string }>,
): string {
  if (selected.length === 0) {
    return t("Keine Auswahl");
  }
  const labels = selected
    .map((value) => options.find((opt) => opt.value === value)?.label ?? value)
    .filter((label) => label.trim() !== "");
  if (labels.length <= 2) {
    return labels.join(", ");
  }
  return `${labels.length} ${t("ausgewählt")}`;
}

function conditionsLabel(entry: ServiceProgramEntryValue): string {
  const count =
    entry.pestCategoryKeys.length +
    entry.pestIndicatorKeys.length +
    entry.monitorTypes.length +
    entry.ioTClasses.length;
  if (count === 0) {
    return t("Immer anzeigen");
  }
  return `${count} ${t("Bedingungen")}`;
}

function matchModeLabel(value: ServiceProgramMatchModeValue): string {
  return value === "all" ? t("Alle") : t("Mindestens eine");
}

function selectedPestLabels(
  values: string[],
  options: TargetOrganismSelectionOption[],
): string[] {
  const labelsByKey = new Map(
    options.map((option) => [option.key, option.label]),
  );
  return values.map((value) => labelsByKey.get(value) ?? value);
}

function conditionsSummaryLines(
  entry: ServiceProgramEntryValue,
  categoryOptions: TargetOrganismSelectionOption[],
  indicatorOptions: TargetOrganismSelectionOption[],
): string[] {
  const lines: string[] = [];
  if (entry.pestCategoryKeys.length > 0) {
    lines.push(
      `${t("Kategorien")} (${matchModeLabel(entry.pestCategoryMatchMode)}): ${selectedPestLabels(entry.pestCategoryKeys, categoryOptions).join(", ")}`,
    );
  }
  if (entry.pestIndicatorKeys.length > 0) {
    lines.push(
      `${t("Indikatoren")} (${matchModeLabel(entry.pestIndicatorMatchMode)}): ${selectedPestLabels(entry.pestIndicatorKeys, indicatorOptions).join(", ")}`,
    );
  }
  if (entry.monitorTypes.length > 0) {
    lines.push(
      `${t("Monitortypen")} (${matchModeLabel(entry.monitorTypeMatchMode)}): ${valuesLabel(entry.monitorTypes, MONITOR_TYPE_OPTIONS)}`,
    );
  }
  if (entry.ioTClasses.length > 0) {
    lines.push(
      `${t("IoT Klassen")} (${matchModeLabel(entry.ioTClassMatchMode)}): ${valuesLabel(entry.ioTClasses, IOT_CLASS_OPTIONS)}`,
    );
  }
  if (lines.length === 0) {
    lines.push(t("Keine Bedingungen gesetzt, wird immer angezeigt."));
  }
  return lines;
}

function MultiSelect<T extends string>({
  title,
  selectedValues,
  options,
  disabled,
  onChange,
}: {
  title: string;
  selectedValues: T[];
  options: Array<{ value: T; label: string }>;
  disabled?: boolean;
  onChange: (next: T[]) => void;
}) {
  const selectedSet = useMemo(() => new Set(selectedValues), [selectedValues]);

  return (
    <div className="grid content-start gap-1.5">
      <Label className="text-xs font-medium">{title}</Label>
      <Popover>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                disabled={disabled}
                aria-label={title}
                className="h-9 w-full cursor-pointer justify-between font-normal"
              >
                <span className="truncate">
                  {valuesLabel(selectedValues, options)}
                </span>
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent>{title}</TooltipContent>
        </Tooltip>
        <PopoverContent className="w-[320px] p-0" align="start">
          <div className="flex items-center justify-end border-b p-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="cursor-pointer"
                  disabled={disabled || selectedValues.length === 0}
                  onClick={() => onChange([])}
                >
                  {t("Alle abwählen")}
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t("Alle abwählen")}</TooltipContent>
            </Tooltip>
          </div>
          <Command>
            <CommandInput placeholder={t("Suchen")} />
            <CommandList>
              <CommandEmpty>{t("Keine Treffer")}</CommandEmpty>
              <CommandGroup>
                {options.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={`${option.value} ${option.label}`}
                    onSelect={() => {
                      const next = selectedSet.has(option.value)
                        ? selectedValues.filter((val) => val !== option.value)
                        : [...selectedValues, option.value];
                      onChange(normalizeEnumValues(next));
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        selectedSet.has(option.value)
                          ? "opacity-100"
                          : "opacity-0",
                      )}
                    />
                    {option.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

function ServiceProgramBodyEditor({
  value,
  disabled,
  showToolbar = true,
  onChange,
}: {
  value: string;
  disabled?: boolean;
  showToolbar?: boolean;
  onChange: (next: string) => void;
}) {
  const editor = useEditor(
    {
      content: value,
      editable: !disabled,
      onUpdate: ({ editor: instance }) => {
        if (!disabled) {
          onChange(editorGenerateHTML(instance));
        }
      },
    },
    [disabled],
  );

  return (
    <Editor
      editor={editor}
      className={cn(
        "bg-background w-full rounded-md border",
        disabled ? "bg-muted/30 opacity-80" : "bg-background",
      )}
    >
      {showToolbar ? (
        <EditorToolbar>
          <EditorBold />
          <EditorItalic />
          <EditorUnderline />
          <EditorToolbarSeparator />
          <EditorBulletList />
          <EditorOrderedList />
          <EditorToolbarSeparator />
          <EditorGesaGreen />
        </EditorToolbar>
      ) : null}
      <EditorContent />
    </Editor>
  );
}

export function ServiceProgramEntryEditor({
  entries,
  groups,
  pageBreaks = [],
  groupsStickyTop = 160,
  disabled,
  showEditControls = true,
  onEntriesChange,
  onGroupsChange,
  onPageBreaksChange,
}: {
  entries: ServiceProgramEntryValue[];
  groups: ServiceProgramGroupValue[];
  pageBreaks?: ServiceProgramPageBreakValue[];
  groupsStickyTop?: number;
  disabled?: boolean;
  showEditControls?: boolean;
  onEntriesChange: (next: ServiceProgramEntryValue[]) => void;
  onGroupsChange: (next: ServiceProgramGroupValue[]) => void;
  onPageBreaksChange?: (next: ServiceProgramPageBreakValue[]) => void;
}) {
  const { companyId } = useActiveCompany();
  const groupContainerRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const groupsHeaderRef = useRef<HTMLDivElement | null>(null);
  const [pendingScrollGroupID, setPendingScrollGroupID] = useState<
    string | null
  >(null);
  const [groupsHeaderHeight, setGroupsHeaderHeight] = useState(0);

  const { data: categoryPests } = useQpmPestListQuery({
    company: { active: true, values: [companyId] },
    kind: PestKind.Category,
  });
  const { data: indicatorPests } = useQpmPestListQuery({
    company: { active: true, values: [companyId] },
    kind: PestKind.Indicator,
  });

  const categoryOptions = useMemo<TargetOrganismSelectionOption[]>(
    () =>
      (categoryPests?.items ?? [])
        .filter((item) => !!item.key)
        .map((item) => ({
          key: item.key!.trim().toLowerCase(),
          label: item.name,
        }))
        .filter((item) => item.key !== "")
        .sort((left, right) => left.label.localeCompare(right.label, "de")),
    [categoryPests?.items],
  );
  const indicatorOptions = useMemo<TargetOrganismSelectionOption[]>(
    () =>
      (indicatorPests?.items ?? [])
        .filter((item) => !!item.key)
        .map((item) => ({
          key: item.key!.trim().toLowerCase(),
          label: item.name,
        }))
        .filter((item) => item.key !== "")
        .sort((left, right) => left.label.localeCompare(right.label, "de")),
    [indicatorPests?.items],
  );

  const sortedGroups = useMemo(() => {
    const normalized = [...groups].map(normalizeGroup);
    return normalized.sort((a, b) => {
      if (a.order !== b.order) {
        return a.order - b.order;
      }
      return (
        serviceProgramSectionOrder(a.section) -
        serviceProgramSectionOrder(b.section)
      );
    });
  }, [groups]);

  const sortedEntries = useMemo(() => {
    const groupsByID = new Map<string, ServiceProgramGroupValue>();
    sortedGroups.forEach((group) => {
      groupsByID.set(group.id, group);
    });
    const defaultGroup = sortedGroups[0];
    return [...entries]
      .map(normalizeEntry)
      .map((entry) => {
        const assignedGroup = groupsByID.get(entry.groupId);
        if (assignedGroup) {
          return {
            ...entry,
            section: assignedGroup.section,
          };
        }
        if (!defaultGroup) {
          return entry;
        }
        return {
          ...entry,
          groupId: defaultGroup.id,
          section: defaultGroup.section,
        };
      })
      .sort((a, b) => {
        if (a.order !== b.order) {
          return a.order - b.order;
        }
        return (
          serviceProgramSectionOrder(a.section) -
          serviceProgramSectionOrder(b.section)
        );
      });
  }, [entries, sortedGroups]);

  const [openMap, setOpenMap] = useState<Record<string, boolean>>({});
  const [isCreateGroupDialogOpen, setIsCreateGroupDialogOpen] = useState(false);
  const [createGroupInsertAfterIndex, setCreateGroupInsertAfterIndex] =
    useState<number | null>(null);
  const [createGroupSection, setCreateGroupSection] =
    useState<string>("maintenance");
  const [createGroupDraft, setCreateGroupDraft] = useState<CreateGroupDraft>(
    emptyCreateGroupDraft(),
  );
  const canEditPageBreaks = showEditControls && Boolean(onPageBreaksChange);

  const setNormalizedEntries = (next: ServiceProgramEntryValue[]) => {
    const withOrder = next.map((entry, idx) => ({
      ...normalizeEntry(entry),
      order: idx + 1,
    }));
    onEntriesChange(withOrder);
  };

  const setNormalizedGroups = (next: ServiceProgramGroupValue[]) => {
    const withOrder = next.map((group, idx) => ({
      ...normalizeGroup(group),
      order: idx + 1,
    }));
    onGroupsChange(withOrder);
  };

  const normalizePageBreaks = (
    next: ServiceProgramPageBreakValue[],
  ): ServiceProgramPageBreakValue[] =>
    next
      .map((item, index) => ({
        ...item,
        id: item.id?.trim() || crypto.randomUUID(),
        anchorId: item.anchorId?.trim() || "",
        order: index + 1,
      }))
      .filter((item) => item.anchorId !== "");

  const setNormalizedPageBreaks = (next: ServiceProgramPageBreakValue[]) => {
    if (!onPageBreaksChange) {
      return;
    }
    const normalized = normalizePageBreaks(next);
    const seen = new Set<string>();
    const deduped = normalized.filter((item) => {
      const key = `${item.anchorKind}:${item.anchorId}`;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
    onPageBreaksChange(deduped);
  };

  const hasPageBreakAt = (anchorKind: "group" | "entry", anchorId: string) =>
    pageBreaks.some(
      (item) => item.anchorKind === anchorKind && item.anchorId === anchorId,
    );

  const togglePageBreak = (anchorKind: "group" | "entry", anchorId: string) => {
    if (!onPageBreaksChange) {
      return;
    }
    const exists = hasPageBreakAt(anchorKind, anchorId);
    if (exists) {
      setNormalizedPageBreaks(
        pageBreaks.filter(
          (item) =>
            !(item.anchorKind === anchorKind && item.anchorId === anchorId),
        ),
      );
      return;
    }
    setNormalizedPageBreaks([
      ...pageBreaks,
      {
        id: crypto.randomUUID(),
        anchorKind,
        anchorId,
        order: pageBreaks.length + 1,
      },
    ]);
  };

  const groupedEntries = useMemo(
    () =>
      sortedGroups.map((group) => ({
        group,
        entries: sortedEntries.filter((entry) => entry.groupId === group.id),
      })),
    [sortedGroups, sortedEntries],
  );

  const flattenGroups = (
    grouped: Array<{
      group: ServiceProgramGroupValue;
      entries: ServiceProgramEntryValue[];
    }>,
  ): ServiceProgramEntryValue[] => {
    const out: ServiceProgramEntryValue[] = [];
    grouped.forEach(({ group, entries: groupEntries }) => {
      groupEntries.forEach((entry) => {
        out.push(
          normalizeEntry({
            ...entry,
            groupId: group.id,
            section: group.section,
          }),
        );
      });
    });
    return out;
  };

  const updateEntry = (entryID: string, next: ServiceProgramEntryValue) => {
    setNormalizedEntries(
      sortedEntries.map((entry) =>
        entry.id === entryID ? normalizeEntry(next) : normalizeEntry(entry),
      ),
    );
  };

  const removeEntry = (entryID: string) => {
    setNormalizedEntries(sortedEntries.filter((entry) => entry.id !== entryID));
  };

  const insertEntryAfter = (groupID: string, entryID: string) => {
    const nextGrouped = groupedEntries.map((item) => {
      if (item.group.id !== groupID) {
        return item;
      }
      const insertIndex = item.entries.findIndex(
        (entry) => entry.id === entryID,
      );
      if (insertIndex < 0) {
        return item;
      }
      const next = [...item.entries];
      const created = emptyEntry(sortedEntries.length + 1);
      created.groupId = item.group.id;
      created.section = item.group.section;
      next.splice(insertIndex + 1, 0, created);
      return { ...item, entries: next };
    });
    setNormalizedEntries(flattenGroups(nextGrouped));
  };

  const addEntryToGroup = (groupID: string) => {
    const group = sortedGroups.find((item) => item.id === groupID);
    if (!group) {
      return;
    }
    const nextGrouped = groupedEntries.map((item) => {
      if (item.group.id !== groupID) {
        return item;
      }
      const created = emptyEntry(sortedEntries.length + 1);
      created.groupId = group.id;
      created.section = group.section;
      return { ...item, entries: [...item.entries, created] };
    });
    setNormalizedEntries(flattenGroups(nextGrouped));
  };

  const updateGroupAt = (index: number, next: ServiceProgramGroupValue) => {
    const normalizedNext = normalizeGroup(next);
    const current = sortedGroups[index];
    setNormalizedGroups(
      sortedGroups.map((group, idx) =>
        idx === index ? normalizedNext : normalizeGroup(group),
      ),
    );
    if (current && current.section !== normalizedNext.section) {
      setNormalizedEntries(
        sortedEntries.map((entry) =>
          entry.groupId === normalizedNext.id
            ? { ...entry, section: normalizedNext.section }
            : entry,
        ),
      );
    }
  };

  const openCreateGroupDialog = (insertAfterIndex: number | null) => {
    const referenceSection =
      insertAfterIndex == null
        ? "maintenance"
        : (sortedGroups[insertAfterIndex]?.section ?? "maintenance");
    setCreateGroupSection(referenceSection);
    setCreateGroupInsertAfterIndex(insertAfterIndex);
    setCreateGroupDraft(emptyCreateGroupDraft());
    setIsCreateGroupDialogOpen(true);
  };

  const addGroup = () => {
    openCreateGroupDialog(null);
  };

  const insertGroupAfter = (index: number) => {
    openCreateGroupDialog(index);
  };

  const moveGroup = (from: number, to: number) => {
    if (from < 0 || from >= sortedGroups.length) {
      return;
    }
    if (to < 0 || to >= sortedGroups.length) {
      return;
    }
    if (from === to) {
      return;
    }
    const movedGroups = arrayMove(sortedGroups, from, to);
    setNormalizedGroups(movedGroups);

    const entriesByGroupID = new Map<string, ServiceProgramEntryValue[]>();
    groupedEntries.forEach(({ group, entries: groupEntries }) => {
      entriesByGroupID.set(group.id, groupEntries.map(normalizeEntry));
    });

    const nextEntries = flattenGroups(
      movedGroups.map((group) => ({
        group,
        entries: entriesByGroupID.get(group.id) ?? [],
      })),
    );
    setNormalizedEntries(nextEntries);
  };

  const removeGroupAt = (index: number) => {
    const removed = sortedGroups[index];
    const nextGroups = sortedGroups.filter((_, idx) => idx !== index);
    setNormalizedGroups(nextGroups);
    if (!removed) {
      return;
    }
    setNormalizedEntries(
      sortedEntries.filter((entry) => entry.groupId !== removed.id),
    );
  };

  const createGroupFromDialog = () => {
    const created = normalizeGroup({
      ...emptyGroup(sortedGroups.length + 1),
      section: createGroupSection,
      title: createGroupDraft.title,
      subtitle: createGroupDraft.subtitle,
    });
    const nextGroups = [...sortedGroups];
    const insertIndex =
      createGroupInsertAfterIndex == null
        ? nextGroups.length
        : createGroupInsertAfterIndex + 1;
    nextGroups.splice(insertIndex, 0, created);
    setNormalizedGroups(nextGroups);
    setPendingScrollGroupID(created.id);
    setIsCreateGroupDialogOpen(false);
    setCreateGroupInsertAfterIndex(null);
  };

  useEffect(() => {
    if (!pendingScrollGroupID) {
      return undefined;
    }

    const node = groupContainerRefs.current[pendingScrollGroupID];
    if (!node) {
      return undefined;
    }

    const rafID = window.requestAnimationFrame(() => {
      node.scrollIntoView({ behavior: "smooth", block: "start" });
      setPendingScrollGroupID(null);
    });

    return () => {
      window.cancelAnimationFrame(rafID);
    };
  }, [groupedEntries, pendingScrollGroupID]);

  useEffect(() => {
    const node = groupsHeaderRef.current;
    if (!node) {
      return () => {};
    }

    const updateHeight = () => {
      setGroupsHeaderHeight(Math.round(node.getBoundingClientRect().height));
    };

    updateHeight();

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(updateHeight);
      observer.observe(node);
      window.addEventListener("resize", updateHeight);
      return () => {
        observer.disconnect();
        window.removeEventListener("resize", updateHeight);
      };
    }

    window.addEventListener("resize", updateHeight);
    return () => {
      window.removeEventListener("resize", updateHeight);
    };
  }, []);

  return (
    <div className="space-y-3">
      <div
        ref={groupsHeaderRef}
        className="bg-card sticky z-20 border-b py-2"
        style={{ top: `${groupsStickyTop}px` }}
      >
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <Label className="text-sm font-semibold">
              {t("Leistungsprogramm Gruppen")}
            </Label>
            <p className="text-muted-foreground text-xs">
              {t(
                "Jede Gruppe enthält Titel, optionalen Untertitel und die zugehörigen Einträge.",
              )}
            </p>
          </div>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="outline"
                onClick={addGroup}
                disabled={disabled || !showEditControls}
                aria-label={t("Gruppe hinzufügen")}
                className={cn(
                  "cursor-pointer",
                  !showEditControls ? "hidden" : "",
                )}
              >
                {`+ ${t("Gruppe hinzufügen")}`}
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t("Gruppe hinzufügen")}</TooltipContent>
          </Tooltip>
        </div>
      </div>

      <div className="space-y-3">
        {groupedEntries.map(({ group, entries: groupEntries }, groupIndex) => (
          <div key={group.id} className="space-y-2">
            {groupIndex > 0 &&
            (canEditPageBreaks || hasPageBreakAt("group", group.id)) ? (
              <PageBreakControl
                active={hasPageBreakAt("group", group.id)}
                disabled={disabled}
                label={t("Seitenumbruch vor Gruppe")}
                onToggle={
                  canEditPageBreaks
                    ? () => togglePageBreak("group", group.id)
                    : undefined
                }
              />
            ) : null}
            <div
              ref={(node) => {
                groupContainerRefs.current[group.id] = node;
              }}
            >
              <ServiceProgramGroupCard
                group={group}
                index={groupIndex}
                entries={groupEntries}
                pageBreaks={pageBreaks}
                disabled={disabled}
                openMap={openMap}
                categoryOptions={categoryOptions}
                indicatorOptions={indicatorOptions}
                showEditControls={showEditControls}
                showPageBreakControls={canEditPageBreaks}
                stickyTop={groupsStickyTop + groupsHeaderHeight}
                canDeleteGroup={sortedGroups.length > 1}
                onToggleOpen={(entryID) =>
                  setOpenMap((curr) => ({
                    ...curr,
                    [entryID]: !(curr[entryID] ?? false),
                  }))
                }
                onUpdateGroup={(next) => updateGroupAt(groupIndex, next)}
                onInsertGroupAfter={() => insertGroupAfter(groupIndex)}
                onMoveGroupUp={() => moveGroup(groupIndex, groupIndex - 1)}
                onMoveGroupDown={() => moveGroup(groupIndex, groupIndex + 1)}
                onDeleteGroup={() => removeGroupAt(groupIndex)}
                canMoveGroupUp={groupIndex > 0}
                canMoveGroupDown={groupIndex < sortedGroups.length - 1}
                onAddEntry={() => addEntryToGroup(group.id)}
                onToggleEntryPageBreak={(entryID) =>
                  togglePageBreak("entry", entryID)
                }
                onUpdateEntry={(entryID, next) => updateEntry(entryID, next)}
                onInsertEntryAfter={(entryID) =>
                  insertEntryAfter(group.id, entryID)
                }
                onRemoveEntry={removeEntry}
                onReorderEntries={(nextGroupEntries) => {
                  const nextGrouped = groupedEntries.map((item) =>
                    item.group.id === group.id
                      ? { ...item, entries: nextGroupEntries }
                      : item,
                  );
                  setNormalizedEntries(flattenGroups(nextGrouped));
                }}
              />
            </div>
          </div>
        ))}
      </div>
      <Dialog
        open={isCreateGroupDialogOpen}
        onOpenChange={setIsCreateGroupDialogOpen}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("Gruppe erstellen")}</DialogTitle>
            <DialogDescription>
              {t(
                "Legen Sie Titel und Untertitel für die neue Gruppe fest. Beide Felder können leer bleiben.",
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid gap-1">
              <Label className="text-xs font-medium">{t("Titel")}</Label>
              <Input
                value={createGroupDraft.title}
                placeholder={t("z.B. Einrichtung des Gemex Schutzsystems")}
                onChange={(event) =>
                  setCreateGroupDraft((curr) => ({
                    ...curr,
                    title: event.target.value,
                  }))
                }
              />
              <p className="text-muted-foreground text-xs">
                {t("Der Titel wird im PDF grün und fett dargestellt.")}
              </p>
            </div>
            <div className="grid gap-1">
              <Label className="text-xs font-medium">{t("Untertitel")}</Label>
              <Textarea
                value={createGroupDraft.subtitle}
                placeholder={t("Optional")}
                rows={1}
                className="resize-none"
                onChange={(event) =>
                  setCreateGroupDraft((curr) => ({
                    ...curr,
                    subtitle: event.target.value,
                  }))
                }
              />
              <p className="text-muted-foreground text-xs">
                {t(
                  "Der Untertitel wird im PDF fett, ohne Haken und unterhalb des Titels dargestellt.",
                )}
              </p>
            </div>
            <p className="text-muted-foreground text-xs">
              {t(
                "Wenn Titel und Untertitel leer sind, wird die Gruppe ohne Überschrift im PDF angezeigt.",
              )}
            </p>
          </div>
          <DialogFooter>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  className="cursor-pointer"
                  onClick={() => setIsCreateGroupDialogOpen(false)}
                >
                  {t("Abbrechen")}
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t("Abbrechen")}</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  className="cursor-pointer"
                  onClick={createGroupFromDialog}
                >
                  {t("Erstellen")}
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t("Gruppe erstellen")}</TooltipContent>
            </Tooltip>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PageBreakControl({
  active,
  disabled,
  label,
  onToggle,
}: {
  active: boolean;
  disabled?: boolean;
  label: string;
  onToggle?: () => void;
}) {
  const canToggle = typeof onToggle === "function";
  return (
    <div className="flex items-center justify-between rounded-md border border-dashed px-2 py-1">
      <div className="text-muted-foreground flex items-center gap-2 text-xs">
        <Scissors className="h-3.5 w-3.5" />
        <span>{label}</span>
      </div>
      {canToggle ? (
        <Button
          type="button"
          size="sm"
          variant={active ? "destructive" : "outline"}
          className="h-7 cursor-pointer px-2 text-xs"
          disabled={disabled}
          onClick={onToggle}
        >
          {active ? t("Entfernen") : t("Einfügen")}
        </Button>
      ) : (
        <span className="text-muted-foreground text-xs font-medium">
          {t("Aktiv")}
        </span>
      )}
    </div>
  );
}

function ServiceProgramGroupCard({
  group,
  index,
  entries,
  pageBreaks,
  disabled,
  openMap,
  categoryOptions,
  indicatorOptions,
  showEditControls,
  showPageBreakControls,
  stickyTop,
  canDeleteGroup,
  onToggleOpen,
  onUpdateGroup,
  onInsertGroupAfter,
  onMoveGroupUp,
  onMoveGroupDown,
  onDeleteGroup,
  canMoveGroupUp,
  canMoveGroupDown,
  onAddEntry,
  onToggleEntryPageBreak,
  onUpdateEntry,
  onInsertEntryAfter,
  onRemoveEntry,
  onReorderEntries,
}: {
  group: ServiceProgramGroupValue;
  index: number;
  entries: ServiceProgramEntryValue[];
  pageBreaks: ServiceProgramPageBreakValue[];
  disabled?: boolean;
  openMap: Record<string, boolean>;
  categoryOptions: TargetOrganismSelectionOption[];
  indicatorOptions: TargetOrganismSelectionOption[];
  showEditControls: boolean;
  showPageBreakControls: boolean;
  stickyTop: number;
  canDeleteGroup: boolean;
  onToggleOpen: (entryID: string) => void;
  onUpdateGroup: (next: ServiceProgramGroupValue) => void;
  onInsertGroupAfter: () => void;
  onMoveGroupUp: () => void;
  onMoveGroupDown: () => void;
  onDeleteGroup: () => void;
  canMoveGroupUp: boolean;
  canMoveGroupDown: boolean;
  onAddEntry: () => void;
  onToggleEntryPageBreak: (entryID: string) => void;
  onUpdateEntry: (entryID: string, next: ServiceProgramEntryValue) => void;
  onInsertEntryAfter: (entryID: string) => void;
  onRemoveEntry: (entryID: string) => void;
  onReorderEntries: (nextGroupEntries: ServiceProgramEntryValue[]) => void;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const title = group.title.trim();
  const displayGroupName = title !== "" ? title : t("Ohne Titel");
  const showSubtitlePreview = !showEditControls && group.subtitle.trim() !== "";
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isGroupOpen, setIsGroupOpen] = useState(true);
  const [isGroupEditing, setIsGroupEditing] = useState(false);
  const entryHasPageBreak = (entryID: string) =>
    pageBreaks.some(
      (item) => item.anchorKind === "entry" && item.anchorId === entryID,
    );

  useEffect(() => {
    if (!showEditControls) {
      setIsGroupEditing(false);
    }
  }, [showEditControls]);

  const onDragEnd = (event: DragEndEvent) => {
    if (disabled) {
      return;
    }
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }
    const oldIndex = entries.findIndex((entry) => entry.id === active.id);
    const newIndex = entries.findIndex((entry) => entry.id === over.id);
    if (oldIndex < 0 || newIndex < 0) {
      return;
    }
    onReorderEntries(arrayMove(entries, oldIndex, newIndex));
  };

  return (
    <Collapsible
      open={isGroupOpen}
      onOpenChange={setIsGroupOpen}
      className="bg-background rounded-lg border shadow-sm"
    >
      <div
        className="bg-background sticky z-10 rounded-t-lg border-b px-3 py-2"
        style={{ top: `${stickyTop}px` }}
      >
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <div className="flex min-w-0 items-center gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="cursor-pointer"
                    onClick={() => setIsGroupOpen((open) => !open)}
                    aria-label={
                      isGroupOpen
                        ? t("Gruppe einklappen")
                        : t("Gruppe ausklappen")
                    }
                  >
                    {isGroupOpen ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronUp className="h-4 w-4" />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {isGroupOpen
                    ? t("Gruppe einklappen")
                    : t("Gruppe ausklappen")}
                </TooltipContent>
              </Tooltip>
              <span className="text-muted-foreground text-sm font-semibold tabular-nums">
                {index + 1}.
              </span>
              <Label className="truncate text-sm font-semibold">
                {displayGroupName}
              </Label>
            </div>
            <div className="flex items-center gap-1">
              {showEditControls ? (
                <>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="cursor-pointer"
                        onClick={onMoveGroupUp}
                        disabled={disabled || !canMoveGroupUp}
                        aria-label={t("Gruppe nach oben verschieben")}
                      >
                        <ArrowUp className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {t("Gruppe eine Position nach oben verschieben")}
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="cursor-pointer"
                        onClick={onMoveGroupDown}
                        disabled={disabled || !canMoveGroupDown}
                        aria-label={t("Gruppe nach unten verschieben")}
                      >
                        <ArrowDown className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {t("Gruppe eine Position nach unten verschieben")}
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="cursor-pointer"
                        onClick={() => setIsGroupEditing((current) => !current)}
                        disabled={disabled}
                        aria-label={
                          isGroupEditing
                            ? t("Gruppenbearbeitung beenden")
                            : t("Gruppe bearbeiten")
                        }
                      >
                        {isGroupEditing ? (
                          <X className="h-4 w-4" />
                        ) : (
                          <Pencil className="h-4 w-4" />
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {isGroupEditing
                        ? t("Gruppenbearbeitung beenden")
                        : t("Gruppe bearbeiten")}
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="cursor-pointer"
                        onClick={onInsertGroupAfter}
                        disabled={disabled}
                        aria-label={t("Gruppe darunter einfügen")}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>{t("Gruppe hinzufügen")}</TooltipContent>
                  </Tooltip>
                  {isGroupEditing ? (
                    <AlertDialog
                      open={isDeleteDialogOpen}
                      onOpenChange={setIsDeleteDialogOpen}
                    >
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <AlertDialogTrigger asChild>
                            <Button
                              type="button"
                              variant="destructive"
                              size="sm"
                              className="cursor-pointer"
                              disabled={disabled || !canDeleteGroup}
                              aria-label={t("Gruppe löschen")}
                              title={t("Gruppe löschen")}
                            >
                              <Trash2 className="h-4 w-4" />
                              {t("Gruppe löschen")}
                            </Button>
                          </AlertDialogTrigger>
                        </TooltipTrigger>
                        <TooltipContent>{t("Gruppe löschen")}</TooltipContent>
                      </Tooltip>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>
                            {t("Gruppe löschen")}
                          </AlertDialogTitle>
                          <AlertDialogDescription>
                            {t(
                              "Diese Änderung kann nicht rückgängig gemacht werden. Alle Einträge dieser Gruppe werden entfernt.",
                            )}
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="cursor-pointer">
                            {t("Abbrechen")}
                          </AlertDialogCancel>
                          <AlertDialogAction
                            className="bg-destructive hover:bg-destructive/90 cursor-pointer text-white"
                            onClick={onDeleteGroup}
                          >
                            {t("Gruppe löschen")}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  ) : null}
                </>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <CollapsibleContent className="px-3 pb-2">
        <div className="space-y-2 pt-3">
          {showEditControls && isGroupEditing ? (
            <div className="grid min-w-0 gap-2">
              <div className="grid min-w-0 gap-1">
                <Label className="text-[11px] font-medium">{t("Titel")}</Label>
                <Input
                  value={group.title}
                  placeholder={t("Leer lassen für Gruppe ohne Titel")}
                  disabled={disabled}
                  className="min-w-0"
                  onChange={(event) =>
                    onUpdateGroup({ ...group, title: event.target.value })
                  }
                />
              </div>
              <div className="grid min-w-0 gap-1">
                <Label className="text-[11px] font-medium">
                  {t("Untertitel")}
                </Label>
                <Textarea
                  value={group.subtitle}
                  placeholder={t("Optional")}
                  disabled={disabled}
                  rows={1}
                  className="resize-none"
                  onChange={(event) =>
                    onUpdateGroup({ ...group, subtitle: event.target.value })
                  }
                />
              </div>
            </div>
          ) : null}
          {!showEditControls && showSubtitlePreview ? (
            <p className="text-muted-foreground text-sm">{group.subtitle}</p>
          ) : null}
        </div>

        <div className="space-y-2 border-t pt-3">
          <div className="flex items-center justify-between">
            <Label className="text-xs font-semibold">{t("Einträge")}</Label>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className={cn(
                    "cursor-pointer",
                    !showEditControls ? "hidden" : "",
                  )}
                  onClick={onAddEntry}
                  disabled={disabled || !showEditControls}
                  aria-label={t("Eintrag hinzufügen")}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t("Eintrag hinzufügen")}</TooltipContent>
            </Tooltip>
          </div>

          {entries.length === 0 ? (
            <div className="text-muted-foreground bg-muted/25 rounded-md p-3 text-xs">
              {t("Keine Einträge in dieser Gruppe")}
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={onDragEnd}
            >
              <SortableContext
                items={entries.map((entry) => entry.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="divide-border/70 divide-y">
                  {entries.map((entry, entryIndex) => (
                    <div key={entry.id} className="space-y-1.5">
                      {showPageBreakControls || entryHasPageBreak(entry.id) ? (
                        <PageBreakControl
                          active={entryHasPageBreak(entry.id)}
                          disabled={disabled}
                          label={t("Seitenumbruch vor Eintrag")}
                          onToggle={
                            showPageBreakControls
                              ? () => onToggleEntryPageBreak(entry.id)
                              : undefined
                          }
                        />
                      ) : null}
                      <ServiceProgramEntryCard
                        entry={entry}
                        index={entryIndex}
                        isOpen={openMap[entry.id] ?? false}
                        disabled={disabled}
                        showEditControls={showEditControls}
                        categoryOptions={categoryOptions}
                        indicatorOptions={indicatorOptions}
                        onToggleOpen={() => onToggleOpen(entry.id)}
                        onUpdate={(next) => onUpdateEntry(entry.id, next)}
                        onInsertAfter={() => onInsertEntryAfter(entry.id)}
                        onRemove={() => onRemoveEntry(entry.id)}
                      />
                    </div>
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

function ServiceProgramEntryCard({
  entry,
  index,
  isOpen,
  disabled,
  showEditControls,
  categoryOptions,
  indicatorOptions,
  onToggleOpen,
  onUpdate,
  onInsertAfter,
  onRemove,
}: {
  entry: ServiceProgramEntryValue;
  index: number;
  isOpen: boolean;
  disabled?: boolean;
  showEditControls: boolean;
  categoryOptions: TargetOrganismSelectionOption[];
  indicatorOptions: TargetOrganismSelectionOption[];
  onToggleOpen: () => void;
  onUpdate: (next: ServiceProgramEntryValue) => void;
  onInsertAfter: () => void;
  onRemove: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: entry.id,
    disabled: Boolean(disabled || !showEditControls),
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState<ServiceProgramEntryValue>(
    normalizeEntry(entry),
  );
  const summaryLines = useMemo(
    () => conditionsSummaryLines(entry, categoryOptions, indicatorOptions),
    [entry, categoryOptions, indicatorOptions],
  );

  useEffect(() => {
    if (!isEditing) {
      setDraft(normalizeEntry(entry));
    }
  }, [entry, isEditing]);

  useEffect(() => {
    if (!showEditControls) {
      setIsEditing(false);
    }
  }, [showEditControls]);

  const saveDraft = () => {
    onUpdate(normalizeEntry(draft));
    setIsEditing(false);
  };

  const cancelEdit = () => {
    setDraft(normalizeEntry(entry));
    setIsEditing(false);
  };

  return (
    <div ref={setNodeRef} style={style} className="space-y-1.5 py-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 flex-1 items-center gap-1.5 text-left">
          {showEditControls && !isEditing ? (
            <button
              {...attributes}
              {...listeners}
              type="button"
              className="hover:bg-muted cursor-grab rounded p-1"
              title={t("Verschieben")}
              disabled={disabled}
            >
              <GripVertical className="text-muted-foreground h-4 w-4" />
            </button>
          ) : null}
          <span className="truncate text-xs font-semibold">{index + 1}.</span>
          <span className="text-muted-foreground text-xs">
            {conditionsLabel(isEditing ? draft : entry)}
          </span>
        </div>
        {showEditControls ? (
          <div className="flex items-center gap-1">
            {isEditing ? (
              <>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="default"
                      size="sm"
                      className="cursor-pointer"
                      disabled={disabled}
                      onClick={saveDraft}
                      aria-label={t("Speichern")}
                    >
                      {t("Speichern")}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{t("Speichern")}</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="destructive"
                      size="icon-sm"
                      className="cursor-pointer"
                      disabled={disabled}
                      onClick={onRemove}
                      aria-label={t("Eintrag löschen")}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{t("Eintrag löschen")}</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon-sm"
                      className="cursor-pointer"
                      disabled={disabled}
                      onClick={cancelEdit}
                      aria-label={t("Abbrechen")}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{t("Abbrechen")}</TooltipContent>
                </Tooltip>
              </>
            ) : (
              <>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      className="cursor-pointer"
                      disabled={disabled}
                      onClick={() => setIsEditing(true)}
                      aria-label={t("Bearbeiten")}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{t("Bearbeiten")}</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      className="cursor-pointer"
                      disabled={disabled}
                      onClick={onInsertAfter}
                      aria-label={t("Darunter einfügen")}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{t("Darunter einfügen")}</TooltipContent>
                </Tooltip>
              </>
            )}
          </div>
        ) : null}
      </div>

      <div className="grid gap-1">
        <ServiceProgramBodyEditor
          value={isEditing ? draft.body : entry.body}
          disabled={disabled || !isEditing}
          showToolbar={showEditControls && isEditing}
          onChange={(body) => setDraft((current) => ({ ...current, body }))}
        />
      </div>

      {!isEditing ? (
        <div className="text-muted-foreground bg-muted/25 rounded-sm px-2 py-1.5 text-xs">
          {summaryLines.map((line) => (
            <div key={line} className="break-words">
              {line}
            </div>
          ))}
        </div>
      ) : null}

      {isEditing ? (
        <Collapsible open={isOpen} onOpenChange={onToggleOpen}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={onToggleOpen}
                className="h-7 cursor-pointer px-2 text-xs"
              >
                {isOpen ? (
                  <ChevronUp className="mr-1 h-3.5 w-3.5" />
                ) : (
                  <ChevronDown className="mr-1 h-3.5 w-3.5" />
                )}
                {isOpen
                  ? t("Bedingungen ausblenden")
                  : t("Bedingungen anzeigen")}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {isOpen ? t("Bedingungen ausblenden") : t("Bedingungen anzeigen")}
            </TooltipContent>
          </Tooltip>

          <CollapsibleContent className="space-y-2 pt-1">
            <div className="bg-muted/35 rounded-md p-2">
              <div className="mb-2 text-[11px] font-medium">
                {t("Bedingungen")}
              </div>
              <div className="space-y-2">
                <TargetOrganismSelectionEditor
                  selection={{
                    categoryKeys: draft.pestCategoryKeys,
                    typeKeys: draft.pestIndicatorKeys,
                  }}
                  disabled={Boolean(disabled)}
                  categoryOptions={categoryOptions}
                  typeOptions={indicatorOptions}
                  onChange={(selection) =>
                    setDraft((current) => ({
                      ...current,
                      pestCategoryKeys: selection.categoryKeys,
                      pestIndicatorKeys: selection.typeKeys,
                    }))
                  }
                  categoryLabel={t("Schädlingskategorien")}
                  typeLabel={t("Schädlingsindikatoren")}
                />
                <div className="grid gap-2 sm:grid-cols-2">
                  <MatchModeSelect
                    title={t("Abgleich Schädlingskategorien")}
                    value={draft.pestCategoryMatchMode}
                    disabled={disabled}
                    onChange={(pestCategoryMatchMode) =>
                      setDraft((current) => ({
                        ...current,
                        pestCategoryMatchMode,
                      }))
                    }
                  />
                  <MatchModeSelect
                    title={t("Abgleich Schädlingsindikatoren")}
                    value={draft.pestIndicatorMatchMode}
                    disabled={disabled}
                    onChange={(pestIndicatorMatchMode) =>
                      setDraft((current) => ({
                        ...current,
                        pestIndicatorMatchMode,
                      }))
                    }
                  />
                </div>
                <div className="grid gap-2 sm:grid-cols-2">
                  <div className="grid gap-2">
                    <MultiSelect
                      title={t("Monitortypen")}
                      selectedValues={draft.monitorTypes}
                      options={MONITOR_TYPE_OPTIONS}
                      disabled={disabled}
                      onChange={(monitorTypes) =>
                        setDraft((current) => ({ ...current, monitorTypes }))
                      }
                    />
                    <MatchModeSelect
                      title={t("Abgleich Monitortypen")}
                      value={draft.monitorTypeMatchMode}
                      disabled={disabled}
                      onChange={(monitorTypeMatchMode) =>
                        setDraft((current) => ({
                          ...current,
                          monitorTypeMatchMode,
                        }))
                      }
                    />
                  </div>
                  <div className="grid gap-2">
                    <MultiSelect
                      title={t("IoT Klassen")}
                      selectedValues={draft.ioTClasses}
                      options={IOT_CLASS_OPTIONS}
                      disabled={disabled}
                      onChange={(ioTClasses) =>
                        setDraft((current) => ({ ...current, ioTClasses }))
                      }
                    />
                    <MatchModeSelect
                      title={t("Abgleich IoT Klassen")}
                      value={draft.ioTClassMatchMode}
                      disabled={disabled}
                      onChange={(ioTClassMatchMode) =>
                        setDraft((current) => ({
                          ...current,
                          ioTClassMatchMode,
                        }))
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>
      ) : null}
    </div>
  );
}
